import { useState, useEffect } from "react";
import { ERROR_MESSAGE_UNEXPECTED_ERROR } from "../../../../../../utils/consts";
import {
  fetchCategoriesInfo,
  fetchMenusInfo,
  fetchProductGetByWrapperInfo,
} from "../../../../../../services/menuApp/menuService";
import { fetchProductWrapperInfo } from "../../../../../../services/menuApp/printerMenuService";

export const useProductData = (
  locationId: string,
  menuId: string,
  productId: string,
  stockDepartmentSelectedNode: any[],
  menus: any[],
  currentMenuData: any,
  reset: any,
) => {
  const [isLoading, setIsLoading] = useState(true);
  const [productData, setProductData] = useState<any>(null);
  const [wrapperData, setWrapperData] = useState<any>(null);
  const [selectedProduct, setSelectedProduct] = useState<any[]>([]);
  const [selectedMenuName, setSelectedMenuName] = useState("");
  const [categoryName, setCategoryName] = useState("");
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [directoryStack, setDirectoryStack] = useState<any[]>([]);
  const [selectedDepartmentObj, setSelectedDepartmentObj] = useState<any>({});
  const [error, setError] = useState("");

  const getProductDetails = async () => {
    try {
      setIsLoading(true);

      // First, try to get product data from Redux cache
      let foundWrapper = null;
      let foundCategory = null;
      let foundDirectoryStack: any[] = [];

      // Search through menuDetails in Redux cache
      if (currentMenuData?.menuNodeList) {
        const searchInNode = (
          node: any,
          targetId: number,
          currentPath: any[] = [],
        ): any => {
          if (!node) return null;
          if (node.id === targetId) {
            return { node, path: [...currentPath, node] };
          }
          if (node.wrapper) {
            for (const key in node.wrapper) {
              const result = searchInNode(node.wrapper[key], targetId, [
                ...currentPath,
                node,
              ]);
              if (result) return result;
            }
          }
          return null;
        };

        // Search in all categories
        if (currentMenuData.menuNodeList[menuId]?.category) {
          for (const catKey in currentMenuData.menuNodeList[menuId].category) {
            const category =
              currentMenuData.menuNodeList[menuId].category[catKey];
            if (category.wrapper) {
              for (const wrapperKey in category.wrapper) {
                const result = searchInNode(
                  category.wrapper[wrapperKey],
                  parseInt(productId),
                );
                console.log("asfasdfgsgdfhdfh", result)
                if (result) {
                  foundWrapper = result.node;
                  foundCategory = category;
                  // Extract directory names from the path
                  const directoryPath = result.path.filter(
                    (p: any) => p.directory === true,
                  );
                  foundDirectoryStack = directoryPath.map((p: any) => ({
                    id: p.id,
                    name: p.name,
                  }));
                  break;
                }
              }
              if (foundWrapper) break;
            }
          }
        }
      }
        

      if (foundWrapper && foundCategory) {
        // Found in cache - use cached data
        setWrapperData(foundWrapper);
        setCategoryName(foundCategory.name);
        setCategoryId(foundCategory.id);
        setDirectoryStack(foundDirectoryStack);

        // Get basic menu info
        const selectedMenu = menus?.find((m: any) => m.id === parseInt(menuId));
        if (selectedMenu) {
          setSelectedMenuName(selectedMenu.name);
        }

        // For product details, we still need to fetch the full product data
        const productRes = await fetchProductGetByWrapperInfo(
          locationId,
          foundWrapper.id,
        );
        if (productRes?.data?.data && productRes.data.data.length > 0) {
          const product = productRes.data.data[0];
          setSelectedProduct(productRes.data.data);
          setProductData(product);

          // Find department
          const department = stockDepartmentSelectedNode.find(
            (dept: any) => dept.id === product.departmentId,
          );
          if (department) {
            setSelectedDepartmentObj(department);
          }
        }

        setIsLoading(false);
        return;
      }

      // If not in cache, fetch from API
      const menuRes = await fetchMenusInfo(locationId);
      const menusData = menuRes.data.data;
      const selectedMenu = menusData.find(
        (m: any) => m.id === parseInt(menuId),
      );

      if (selectedMenu) {
        setSelectedMenuName(selectedMenu.name);
      }

      const categoriesRes = await fetchCategoriesInfo(locationId);
      const categories = categoriesRes.data.data;
      const wrapperRes = await fetchProductWrapperInfo(locationId);
      const wrappers = wrapperRes.data.data;

      const findProductWrapper = (
        wrapperList: any[],
        targetProductId: number,
        currentPath: any[] = [],
      ): any => {
        for (const wrapper of wrapperList) {
          if (wrapper.id === targetProductId) {
            return { wrapper, path: currentPath };
          }
          if (wrapper.wrapper && Object.values(wrapper.wrapper).length > 0) {
            const found = findProductWrapper(
              Object.values(wrapper.wrapper),
              targetProductId,
              [...currentPath, wrapper],
            );
            if (found) return found;
          }
        }
        return null;
      };

      const result = findProductWrapper(wrappers, parseInt(productId));
      const foundWrapperAPI = result?.wrapper;
      const directoryPath = result?.path || [];

      if (foundWrapperAPI) {
        setWrapperData(foundWrapperAPI);

        // Build directory stack from path
        const directoryStackFromPath = directoryPath
          .filter((p: any) => p.directory === true)
          .map((p: any) => ({ id: p.id, name: p.name }));
        setDirectoryStack(directoryStackFromPath);

        // Find category
        const actualCategoryId =
          typeof foundWrapperAPI.categoryId === "object"
            ? foundWrapperAPI.categoryId.id
            : foundWrapperAPI.categoryId;
        const category = categories.find((c: any) => c.id === actualCategoryId);
        if (category) {
          setCategoryName(category.name);
          setCategoryId(category.id);
        }

        const productRes = await fetchProductGetByWrapperInfo(
          locationId,
          foundWrapperAPI.id,
        );
        if (productRes?.data?.data && productRes.data.data.length > 0) {
          const product = productRes.data.data[0];
          setSelectedProduct(productRes.data.data);
          setProductData(product);

          const department = stockDepartmentSelectedNode.find(
            (dept: any) => dept.id === product.departmentId,
          );
          if (department) {
            setSelectedDepartmentObj(department);
          }
        }
      }

      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching product details:", error);
      setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
      setIsLoading(false);
    }
  };

  // Reset form when product data is loaded
  useEffect(() => {
    if (productData && wrapperData && reset) {
      reset({
        name: wrapperData.name || "",
        barcode: wrapperData.barcode || "",
        plu: wrapperData.plu || "",
        upcBarcode: wrapperData.upcBarcode || "",
        cost: wrapperData.cost?.toString() || "0",
        desc: wrapperData.desc || "",
        price: productData.price?.toString() || "0",
        printName: wrapperData.printName || "",
        size: wrapperData.size || "",
        slavePrintType: wrapperData.slavePrintType || "",
        cover: wrapperData.cover || "",
        itemType: wrapperData.itemType || "",
        nameShort: wrapperData.nameShort || "",
        img: wrapperData.img || "",
      });
    }
  }, [productData, wrapperData, reset]);

  useEffect(() => {
    if (stockDepartmentSelectedNode.length > 0) {
      getProductDetails();
    }
  }, [productId, stockDepartmentSelectedNode]);

  return {
    isLoading,
    productData,
    wrapperData,
    selectedProduct,
    selectedMenuName,
    categoryName,
    categoryId,
    directoryStack,
    selectedDepartmentObj,
    setSelectedDepartmentObj,
    error,
    setError,
    setProductData,
    setWrapperData,
    setSelectedProduct,
  };
};
